import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getErrorMessage } from '../utils/errorHandler';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus, Search, Package, AlertTriangle, Calendar, Loader2, Edit, Trash2,
  ArrowUpCircle, ArrowDownCircle, RotateCw
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIE = [
  { value: 'pasta', label: 'Pasta' }, { value: 'riso', label: 'Riso' },
  { value: 'olio', label: 'Olio' }, { value: 'latte', label: 'Latte' },
  { value: 'biscotti', label: 'Biscotti' }, { value: 'farina', label: 'Farina' },
  { value: 'passata', label: 'Passata' }, { value: 'scatolame', label: 'Scatolame' },
  { value: 'prodotti_infanzia', label: 'Prodotti Infanzia' }, { value: 'pannolini', label: 'Pannolini' },
  { value: 'igiene_personale', label: 'Igiene Personale' }, { value: 'pulizia_casa', label: 'Pulizia Casa' },
  { value: 'altro', label: 'Altro' },
];

const ProductForm = ({ prodotto, onSave, onClose }) => {
  const [formData, setFormData] = useState(prodotto || {
    nome_prodotto: '', categoria: 'pasta', unita_misura: 'pz',
    quantita_disponibile: 0, quantita_minima: 5, valore_unitario: 0,
    data_scadenza: '', lotto: '', fornitore: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await onSave(formData); onClose(); }
    catch (err) { toast.error(getErrorMessage(err, 'Errore')); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto px-1">
      <div className="space-y-1.5">
        <Label>Nome Prodotto *</Label>
        <Input value={formData.nome_prodotto} onChange={(e) => setFormData({ ...formData, nome_prodotto: e.target.value })} required data-testid="prodotto-nome-input" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Categoria *</Label>
          <Select value={formData.categoria} onValueChange={(v) => setFormData({ ...formData, categoria: v })}>
            <SelectTrigger data-testid="prodotto-categoria-select"><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORIE.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Unita Misura</Label>
          <Select value={formData.unita_misura} onValueChange={(v) => setFormData({ ...formData, unita_misura: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pz">Pezzi</SelectItem>
              <SelectItem value="kg">Kg</SelectItem>
              <SelectItem value="lt">Litri</SelectItem>
              <SelectItem value="conf">Confezioni</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="space-y-1.5">
          <Label>Quantita</Label>
          <Input type="number" min="0" value={formData.quantita_disponibile} onChange={(e) => setFormData({ ...formData, quantita_disponibile: parseInt(e.target.value) || 0 })} data-testid="prodotto-qta-input" />
        </div>
        <div className="space-y-1.5">
          <Label>Scorta Min.</Label>
          <Input type="number" min="0" value={formData.quantita_minima} onChange={(e) => setFormData({ ...formData, quantita_minima: parseInt(e.target.value) || 0 })} />
        </div>
        <div className="space-y-1.5">
          <Label>Valore Unit.</Label>
          <Input type="number" step="0.01" min="0" value={formData.valore_unitario} onChange={(e) => setFormData({ ...formData, valore_unitario: parseFloat(e.target.value) || 0 })} />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label>Data Scadenza</Label>
          <Input type="date" value={formData.data_scadenza} onChange={(e) => setFormData({ ...formData, data_scadenza: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label>Lotto</Label>
          <Input value={formData.lotto} onChange={(e) => setFormData({ ...formData, lotto: e.target.value })} />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Fornitore</Label>
        <Input value={formData.fornitore} onChange={(e) => setFormData({ ...formData, fornitore: e.target.value })} />
      </div>
      <DialogFooter className="flex-col sm:flex-row gap-2 sticky bottom-0 bg-background pb-1">
        <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto">Annulla</Button>
        <Button type="submit" disabled={loading} className="w-full sm:w-auto" data-testid="prodotto-submit-btn">
          {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          {prodotto ? 'Aggiorna' : 'Crea'}
        </Button>
      </DialogFooter>
    </form>
  );
};

const MovimentoForm = ({ prodotti, onSave, onClose }) => {
  const [formData, setFormData] = useState({ tipo_movimento: 'entrata', prodotto_id: '', quantita: 1, note: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try { await onSave(formData); onClose(); }
    catch (err) { toast.error(getErrorMessage(err, 'Errore')); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Tipo Movimento *</Label>
        <Select value={formData.tipo_movimento} onValueChange={(v) => setFormData({ ...formData, tipo_movimento: v })}>
          <SelectTrigger data-testid="movimento-tipo-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="entrata">Entrata</SelectItem>
            <SelectItem value="uscita">Uscita</SelectItem>
            <SelectItem value="rettifica">Rettifica</SelectItem>
            <SelectItem value="scadenza">Scadenza</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Prodotto *</Label>
        <Select value={formData.prodotto_id} onValueChange={(v) => setFormData({ ...formData, prodotto_id: v })}>
          <SelectTrigger data-testid="movimento-prodotto-select"><SelectValue placeholder="Seleziona prodotto" /></SelectTrigger>
          <SelectContent>{prodotti.map((p) => <SelectItem key={p.id} value={p.id}>{p.nome_prodotto}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Quantita *</Label>
        <Input type="number" min="1" value={formData.quantita} onChange={(e) => setFormData({ ...formData, quantita: parseInt(e.target.value) || 1 })} data-testid="movimento-qta-input" />
      </div>
      <div className="space-y-2">
        <Label>Note</Label>
        <Input value={formData.note} onChange={(e) => setFormData({ ...formData, note: e.target.value })} />
      </div>
      <DialogFooter className="flex-col sm:flex-row gap-2">
        <Button type="button" variant="outline" onClick={onClose} className="w-full sm:w-auto">Annulla</Button>
        <Button type="submit" disabled={loading || !formData.prodotto_id} className="w-full sm:w-auto" data-testid="movimento-submit-btn">
          {loading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          Registra
        </Button>
      </DialogFooter>
    </form>
  );
};

const ProdottoMobileCard = ({ p, isAdmin, openEditProduct, handleDeleteProduct }) => (
  <div className="mobile-card-item" data-testid={`prodotto-card-${p.id}`}>
    <div className="flex items-start justify-between gap-2">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm">{p.nome_prodotto}</span>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
            {CATEGORIE.find(c => c.value === p.categoria)?.label || p.categoria}
          </Badge>
        </div>
        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
          <span className="flex items-center gap-1">
            {p.sotto_scorta && <AlertTriangle className="w-3 h-3 text-amber-500" />}
            <span className={p.sotto_scorta ? 'text-amber-600 font-medium' : ''}>{p.quantita_disponibile}</span>
            <span>/ {p.quantita_minima}</span>
          </span>
          {p.data_scadenza && (
            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(p.data_scadenza).toLocaleDateString('it-IT')}</span>
          )}
          <span>{p.valore_unitario.toFixed(2)}/u</span>
        </div>
      </div>
      {isAdmin() && (
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => openEditProduct(p)} data-testid={`edit-prodotto-${p.id}`}>
            <Edit className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-9 w-9 text-destructive" onClick={() => handleDeleteProduct(p.id)}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  </div>
);

const MovimentoMobileCard = ({ m, getMovimentoIcon }) => (
  <div className="mobile-card-item">
    <div className="flex items-center justify-between gap-2">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          {getMovimentoIcon(m.tipo_movimento)}
          <span className="font-medium text-sm capitalize">{m.tipo_movimento}</span>
          <span className="text-xs text-muted-foreground">{new Date(m.data).toLocaleDateString('it-IT')}</span>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{m.prodotto_nome}</p>
      </div>
      <span className={`font-medium text-sm ${m.tipo_movimento === 'entrata' ? 'text-green-600' : m.tipo_movimento === 'uscita' ? 'text-red-600' : ''}`}>
        {m.tipo_movimento === 'entrata' ? '+' : m.tipo_movimento === 'uscita' ? '-' : ''}{m.quantita}
      </span>
    </div>
  </div>
);

export const MagazzinoPage = () => {
  const { API, isAdmin } = useAuth();
  const [prodotti, setProdotti] = useState([]);
  const [movimenti, setMovimenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoria, setCategoria] = useState('');
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [movimentoDialogOpen, setMovimentoDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  useEffect(() => { fetchData(); }, [categoria]);

  const fetchData = async () => {
    try {
      const params = categoria ? { categoria } : {};
      const [prodottiRes, movimentiRes] = await Promise.all([
        axios.get(`${API}/prodotti`, { params }),
        axios.get(`${API}/movimenti`, { params: { limit: 50 } })
      ]);
      setProdotti(prodottiRes.data);
      setMovimenti(movimentiRes.data);
    } catch (err) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProduct = async (data) => {
    if (editingProduct) {
      await axios.put(`${API}/prodotti/${editingProduct.id}`, data);
      toast.success('Prodotto aggiornato');
    } else {
      await axios.post(`${API}/prodotti`, data);
      toast.success('Prodotto creato');
    }
    fetchData();
    setEditingProduct(null);
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Eliminare questo prodotto?')) return;
    try {
      await axios.delete(`${API}/prodotti/${id}`);
      toast.success('Prodotto eliminato');
      fetchData();
    } catch (err) {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const handleSaveMovimento = async (data) => {
    await axios.post(`${API}/movimenti`, data);
    toast.success('Movimento registrato');
    fetchData();
  };

  const openEditProduct = (p) => { setEditingProduct(p); setProductDialogOpen(true); };
  const openCreateProduct = () => { setEditingProduct(null); setProductDialogOpen(true); };

  const filteredProdotti = prodotti.filter(p =>
    p.nome_prodotto.toLowerCase().includes(search.toLowerCase())
  );

  const getMovimentoIcon = (tipo) => {
    switch (tipo) {
      case 'entrata': return <ArrowDownCircle className="w-4 h-4 text-green-600" />;
      case 'uscita': return <ArrowUpCircle className="w-4 h-4 text-red-600" />;
      case 'rettifica': return <RotateCw className="w-4 h-4 text-blue-600" />;
      case 'scadenza': return <AlertTriangle className="w-4 h-4 text-amber-600" />;
      default: return null;
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="magazzino-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold">Magazzino</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Gestisci i prodotti e i movimenti</p>
        </div>
        <div className="flex gap-2">
          {isAdmin() && (
            <>
              <Dialog open={movimentoDialogOpen} onOpenChange={setMovimentoDialogOpen}>
                <Button variant="outline" onClick={() => setMovimentoDialogOpen(true)} className="flex-1 sm:flex-none" data-testid="add-movimento-btn">
                  <RotateCw className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="sm:inline">Movimento</span>
                </Button>
                <DialogContent>
                  <DialogHeader><DialogTitle>Nuovo Movimento</DialogTitle></DialogHeader>
                  <MovimentoForm prodotti={prodotti} onSave={handleSaveMovimento} onClose={() => setMovimentoDialogOpen(false)} />
                </DialogContent>
              </Dialog>

              <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
                <Button onClick={openCreateProduct} className="flex-1 sm:flex-none" data-testid="add-prodotto-btn">
                  <Plus className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="sm:inline">Prodotto</span>
                </Button>
                <DialogContent className="sm:max-w-lg">
                  <DialogHeader><DialogTitle>{editingProduct ? 'Modifica Prodotto' : 'Nuovo Prodotto'}</DialogTitle></DialogHeader>
                  <ProductForm prodotto={editingProduct} onSave={handleSaveProduct} onClose={() => setProductDialogOpen(false)} />
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input placeholder="Cerca prodotto..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="magazzino-search-input" />
        </div>
        <Select value={categoria || "all"} onValueChange={(v) => setCategoria(v === "all" ? "" : v)}>
          <SelectTrigger className="sm:w-48" data-testid="magazzino-categoria-filter"><SelectValue placeholder="Tutte le categorie" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le categorie</SelectItem>
            {CATEGORIE.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="prodotti">
        <TabsList className="w-full sm:w-auto">
          <TabsTrigger value="prodotti" className="flex-1 sm:flex-none" data-testid="tab-prodotti">Prodotti ({prodotti.length})</TabsTrigger>
          <TabsTrigger value="movimenti" className="flex-1 sm:flex-none" data-testid="tab-movimenti">Movimenti</TabsTrigger>
        </TabsList>

        <TabsContent value="prodotti" className="mt-4 sm:mt-6">
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-4 sm:p-6 space-y-3">
                  {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
                </div>
              ) : filteredProdotti.length === 0 ? (
                <div className="p-8 sm:p-12 text-center">
                  <Package className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-muted-foreground/50" />
                  <p className="mt-3 text-sm text-muted-foreground">Nessun prodotto trovato</p>
                </div>
              ) : (
                <>
                  {/* Desktop Table */}
                  <div className="hidden md:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Prodotto</TableHead>
                          <TableHead>Categoria</TableHead>
                          <TableHead className="text-center">Quantita</TableHead>
                          <TableHead className="hidden lg:table-cell">Scadenza</TableHead>
                          <TableHead className="hidden lg:table-cell text-right">Valore</TableHead>
                          <TableHead className="text-right">Azioni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredProdotti.map((p) => (
                          <TableRow key={p.id} data-testid={`prodotto-row-${p.id}`}>
                            <TableCell>
                              <div className="font-medium">{p.nome_prodotto}</div>
                              {p.lotto && <div className="text-xs text-muted-foreground">Lotto: {p.lotto}</div>}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{CATEGORIE.find(c => c.value === p.categoria)?.label || p.categoria}</Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex items-center justify-center gap-2">
                                {p.sotto_scorta && <AlertTriangle className="w-4 h-4 text-amber-500" />}
                                <span className={`font-medium ${p.sotto_scorta ? 'text-amber-600' : ''}`}>{p.quantita_disponibile}</span>
                                <span className="text-muted-foreground text-sm">/ {p.quantita_minima}</span>
                              </div>
                            </TableCell>
                            <TableCell className="hidden lg:table-cell">
                              {p.data_scadenza ? (
                                <div className="flex items-center gap-1"><Calendar className="w-4 h-4 text-muted-foreground" />{new Date(p.data_scadenza).toLocaleDateString('it-IT')}</div>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="hidden lg:table-cell text-right">{p.valore_unitario.toFixed(2)}</TableCell>
                            <TableCell className="text-right">
                              {isAdmin() && (
                                <div className="flex items-center justify-end gap-1">
                                  <Button variant="ghost" size="icon" onClick={() => openEditProduct(p)} data-testid={`edit-prodotto-${p.id}`}><Edit className="w-4 h-4" /></Button>
                                  <Button variant="ghost" size="icon" onClick={() => handleDeleteProduct(p.id)} className="text-destructive"><Trash2 className="w-4 h-4" /></Button>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {/* Mobile Card List */}
                  <div className="md:hidden">
                    {filteredProdotti.map((p) => (
                      <ProdottoMobileCard key={p.id} p={p} isAdmin={isAdmin} openEditProduct={openEditProduct} handleDeleteProduct={handleDeleteProduct} />
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="movimenti" className="mt-4 sm:mt-6">
          <Card>
            <CardContent className="p-0">
              {movimenti.length === 0 ? (
                <div className="p-8 sm:p-12 text-center">
                  <RotateCw className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-muted-foreground/50" />
                  <p className="mt-3 text-sm text-muted-foreground">Nessun movimento registrato</p>
                </div>
              ) : (
                <>
                  {/* Desktop Table */}
                  <div className="hidden md:block">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Data</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead>Prodotto</TableHead>
                          <TableHead className="text-center">Quantita</TableHead>
                          <TableHead className="hidden lg:table-cell">Operatore</TableHead>
                          <TableHead className="hidden lg:table-cell">Note</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {movimenti.map((m) => (
                          <TableRow key={m.id}>
                            <TableCell>{new Date(m.data).toLocaleDateString('it-IT')}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">{getMovimentoIcon(m.tipo_movimento)}<span className="capitalize">{m.tipo_movimento}</span></div>
                            </TableCell>
                            <TableCell>{m.prodotto_nome}</TableCell>
                            <TableCell className="text-center font-medium">
                              {m.tipo_movimento === 'entrata' ? '+' : m.tipo_movimento === 'uscita' ? '-' : ''}{m.quantita}
                            </TableCell>
                            <TableCell className="hidden lg:table-cell">{m.operatore_nome}</TableCell>
                            <TableCell className="hidden lg:table-cell text-muted-foreground">{m.note || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {/* Mobile Card List */}
                  <div className="md:hidden">
                    {movimenti.map((m) => <MovimentoMobileCard key={m.id} m={m} getMovimentoIcon={getMovimentoIcon} />)}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MagazzinoPage;
